/**
 * CourseAditionalMaterial Component
 *
 * A comprehensive React component that displays a list of additional materials (files/documents)
 * associated with a course. The component adapts its behavior and UI based on the user's access level:
 * - With access: Materials are clickable and open in new tabs
 * - Without access: Materials are displayed as read-only items
 *
 * Features:
 * - Dynamic rendering of course materials based on access permissions
 * - Clickable file links for authorized users
 * - Responsive design for both access and no-access states
 * - Multi-language support via i18n
 * - Accessibility with alt text for icons
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Array} props.aditionalMaterial - Array of material objects containing file information
 * @param {boolean} props.accessCourse - Whether the user has access to the course materials
 * @returns {JSX.Element} The rendered additional materials section
 */

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import classes from './CourseAditionalMaterial.module.css';

const CourseAditionalMaterial = props => {
  const { t } = useTranslation(['course']);

  /**
   * Creates a function to open a file in a new browser tab
   * @param {string} url - The URL of the file (currently unused)
   * @param {string} path - The file path to open
   * @returns {Function} Event handler function
   */
  const openFile = (url, path) => () => {
    window.open(`${path}`, '_blank');
  };

  return (
    <div
      className={
        props.accessCourse
          ? classes.productLearnInfoAccessSection
          : classes.productLearnInfoSection
      }
    >
      <h2 className={classes.includesTitle}>
        {t('aditional_files')}
        {/* Internationalized section title */}
      </h2>
      {/* Render materials list only if materials exist */}
      {props.aditionalMaterial && (
        <aside className={classes.productContentItemsContainer}>
          {props.aditionalMaterial.map(material => (
            <p
              key={material.idArchivoVideo}
              className={`${
                props.accessCourse ? classes.materialClick : ''
              }`}
              onClick={
                /* Only allow clicking if user has course access */
                props.accessCourse &&
                openFile(material.isURL, material.path)
              }
            >
              {/* Material file icon */}
              <figure className={classes.productIcon}>
                <img
                  className={classes.img}
                  src="https://d1g6c01s9ssd7h.cloudfront.net/img/Documento_icon.png"
                  alt="Document icon" // Accessibility: icon alt text
                />
              </figure>
              {/* Material name/title */}
              <span className={classes.productContentItemText}>
                {material.name}{' '}
                {/* File/document name passed as prop to this span */}
              </span>
            </p>
          ))}
        </aside>
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseAditionalMaterial.propTypes = {
  /**
   * Array of additional material objects for the course
   * Each material object should contain:
   * - idArchivoVideo: unique identifier for the material
   * - name: display name of the material
   * - path: file path or URL to the material
   * - isURL: boolean indicating if the path is a URL
   */
  aditionalMaterial: PropTypes.arrayOf(
    PropTypes.shape({
      idArchivoVideo: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      name: PropTypes.string, // File/document name used in child span
      path: PropTypes.string, // File/document path used for opening
      isURL: PropTypes.bool // Indicates if path is a URL
    })
  ),
  /**
   * Boolean indicating whether the user has access to the course materials
   * When true, materials become clickable and open in new tabs
   * Passed as prop to control click behavior and styling
   */
  accessCourse: PropTypes.bool
};

CourseAditionalMaterial.defaultProps = {
  aditionalMaterial: [],
  accessCourse: false
};

export default CourseAditionalMaterial;
